/**
 * src/components/agent/ScopeChipPicker.styles.js — TICKET-1145.
 *
 * The scope picker as drawn in the "Chat Scope Bar" design (Picker artboard):
 * a large search field, filter pills with counts, 56px rows with a type badge
 * and a check, a summary + "Apply scope" footer. Phone: full-height bottom sheet.
 * Desktop: centred panel. Tokens only (colour pairs per src/components/CLAUDE.md).
 */

// Per-type tone: saturated text token + its tint background.
export const TYPE_TONE = {
  project: { fg: 'var(--info)', bg: 'var(--info-tint)' },
  deal: { fg: 'var(--warn)', bg: 'var(--warn-tint)' },
  task: { fg: 'var(--ok)', bg: 'var(--ok-tint)' },
  person: { fg: 'var(--t2)', bg: 'var(--glass)' },
};

export const scrimStyle = {
  position: 'fixed',
  inset: 0,
  zIndex: 1290,
  background: 'var(--scrim)',
};

export const sheetStyle = (narrow) => ({
  position: 'fixed',
  zIndex: 1300,
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
  boxSizing: 'border-box',
  background: 'var(--card, var(--bg))',
  border: '1px solid var(--bdr)',
  boxShadow: 'var(--shc)',
  padding: 16,
  ...(narrow
    ? {
        left: 0,
        right: 0,
        bottom: 0,
        height: '88vh',
        borderRadius: '16px 16px 0 0',
        paddingBottom: 'calc(16px + env(safe-area-inset-bottom, 0px))',
      }
    : {
        left: '50%',
        top: '50%',
        transform: 'translate(-50%, -50%)',
        width: 'min(640px, calc(100vw - 32px))',
        height: 'min(700px, calc(100vh - 64px))',
        borderRadius: 16,
      }),
});

export const searchWrapStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  minHeight: 48,
  padding: '0 14px',
  borderRadius: 12,
  background: 'var(--bg)',
  border: '1px solid var(--accent)',
  color: 'var(--tm)',
};

export const searchInputStyle = {
  flex: 1,
  minWidth: 0,
  minHeight: 44,
  border: 'none',
  background: 'transparent',
  color: 'var(--t1)',
  fontSize: 16, // ≥16px: iOS does not zoom the page on focus
  fontFamily: 'inherit',
  outline: 'none',
};

export const filterRowStyle = {
  display: 'flex',
  flexWrap: 'wrap', // every type stays visible on a phone (no hidden sideways scroll)
  gap: 8,
  flexShrink: 0,
};

export const filterPillStyle = (active) => ({
  flexShrink: 0,
  minHeight: 44,
  padding: '8px 14px',
  borderRadius: 22,
  border: 'none',
  outline: active ? 'none' : '1px solid var(--bdr)',
  outlineOffset: -1,
  background: active ? 'var(--navy-surface)' : 'transparent',
  color: active ? 'var(--on-navy)' : 'var(--t2)',
  fontSize: 13,
  fontWeight: 600,
  fontFamily: 'inherit',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
});

export const listStyle = {
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
  WebkitOverflowScrolling: 'touch',
};

export const sectionTitleStyle = {
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: 'var(--tm)',
  padding: '10px 4px 4px',
};

export const rowStyle = (type, on, focused) => {
  const tone = TYPE_TONE[type] || TYPE_TONE.person;
  return {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    width: '100%',
    minHeight: 56,
    padding: '8px 12px',
    borderRadius: 10,
    border: 'none',
    outline: on ? `1px solid ${tone.fg}` : focused ? '1px solid var(--accent)' : '1px solid var(--bdr)',
    outlineOffset: -1,
    background: on ? tone.bg : 'var(--bg)',
    color: 'var(--t1)',
    fontFamily: 'inherit',
    textAlign: 'left',
    cursor: 'pointer',
  };
};

export const badgeStyle = (type) => {
  const tone = TYPE_TONE[type] || TYPE_TONE.person;
  return {
    width: 32,
    height: 32,
    flexShrink: 0,
    borderRadius: 8,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 600,
    fontSize: 14,
    background: tone.bg,
    color: tone.fg,
  };
};

export const footerStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  borderTop: '1px solid var(--bdr)',
  paddingTop: 12,
  flexShrink: 0,
};

export const applyBtnStyle = (disabled) => ({
  minHeight: 44,
  padding: '8px 18px',
  borderRadius: 10,
  border: 'none',
  background: 'var(--navy-surface)',
  color: 'var(--on-navy)',
  fontSize: 14,
  fontWeight: 600,
  fontFamily: 'inherit',
  cursor: disabled ? 'default' : 'pointer',
  opacity: disabled ? 0.5 : 1,
  flexShrink: 0,
});

export const closeBtnStyle = {
  minHeight: 44,
  minWidth: 44,
  padding: '8px 12px',
  border: 'none',
  borderRadius: 10,
  background: 'transparent',
  color: 'var(--tm)',
  fontSize: 13,
  fontFamily: 'inherit',
  cursor: 'pointer',
};
